import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import logo from '../assets/logo.png';
import Timeline from './Timeline.js';
import About from './About.js';
import Skills from './Skills.js';
import Contact from './Contact.js';
import FeaturedProject from './FeaturedProject.js';
import PaintTitle from './PaintTitle.js';
import QuantFeature from './QuantFeature.js';
import { ElephantFind } from './ElephantHunt';
import SnapCubeFeature from './SnapCubeFeature.js';

const ParticleBackground = ({ themeId }) => {
    const [particles, setParticles] = useState([]);

    useEffect(() => {
        const particleCount = themeId === 'paintwash' ? 0 : themeId === 'sunset' ? 18 : themeId === 'museum' ? 14 : themeId === 'manuscript' ? 12 : 50;

        if (particleCount === 0) {
            setParticles([]);
            return;
        }

        const newParticles = Array.from({ length: particleCount }).map(() => ({
            id: Math.random(),
            size: Math.random() * 3 + 1,
            left: Math.random() * 100,
            animationDuration: Math.random() * 3 + 2,
            animationDelay: Math.random() * 5,
        }));
        setParticles(newParticles);
    }, [themeId]);

    return (
        <div className="particles-bg">
            {particles.map(p => (
                <div
                    key={p.id}
                    className="particle"
                    style={{
                        width: `${p.size}px`,
                        height: `${p.size}px`,
                        left: `${p.left}%`,
                        animationDuration: `${p.animationDuration}s`,
                        animationDelay: `${p.animationDelay}s`,
                    }}
                ></div>
            ))}
        </div>
    );
};


function HomePage({ projects, featuredProject, themeId }) {
    const [isNavOpen, setIsNavOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 768px)').matches);
    useEffect(() => {
        const media = window.matchMedia('(max-width: 768px)');
        const update = () => setIsMobile(media.matches);
        update();
        media.addEventListener('change', update);
        return () => media.removeEventListener('change', update);
    }, []);
    const [searchParams] = useSearchParams();
    const isQuant = searchParams.get('focus') === 'quant';
    const focusQuery = isQuant ? '?focus=quant' : '';
    const snapCube = projects.find(project => project.id === 'ar-rubiks-cube-guide');
    const quantProject = projects.find(project => project.id === 'statistical-arbitrage-engine');
    const displayName = 'Kyle Perlman';

    const handleNavLinkClick = () => {
        setIsNavOpen(false);
    };

    return (
        <div>
            <header className="navbar">
                <div className="navbar-inner">
                    <div className="logo-title">
                        <span className="elephant-logo-wrap"><img src={logo} alt="Swirl logo" className="nav-logo" /><ElephantFind id="navbar" className="elephant-navbar" /></span>
                        <span className="nav-name">{displayName}</span>
                    </div>

                    <button
                        className={`hamburger-menu ${isNavOpen ? 'active' : ''}`}
                        onClick={() => setIsNavOpen(!isNavOpen)}
                        aria-label="Toggle navigation"
                        aria-expanded={isNavOpen}
                        aria-controls="main-navigation"
                    >
                        <span className="hamburger-bar"></span>
                        <span className="hamburger-bar"></span>
                        <span className="hamburger-bar"></span>
                    </button>

                    <nav id="main-navigation" className={`nav-links ${isNavOpen ? 'active' : ''}`}>
                        <a href="#about" onClick={handleNavLinkClick}>About</a>
                        <a href="#featured" onClick={handleNavLinkClick}>Featured</a>
                        {isQuant && <a href="#quant" onClick={handleNavLinkClick}>Quant</a>}
                        <a href="#projects" onClick={handleNavLinkClick}>Projects</a>
                        <a href="#skills" onClick={handleNavLinkClick}>Skills</a>
                        <a href="#contact" onClick={handleNavLinkClick}>Contact</a>
                    </nav>
                </div>
            </header>

            {/* HERO */}
            <section className="hero" id="home">
                <ParticleBackground themeId={themeId} />
                <div className="hero-content">
                    <p className="hero-eyebrow">Kyle Perlman / {isQuant ? "Quantitative Research and Development" : "Electrical and Computer Engineering"}</p>
                    <PaintTitle as="h1" themed={themeId !== 'paintwash'} singleLine={!isMobile && !['swiss', 'museum', 'manuscript'].includes(themeId)}>Hardware, software, and everything between.</PaintTitle>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.5 }}
                    >
                        {isQuant
                            ? "I'm Kyle Perlman, an electrical and computer engineer applying signals, estimation, and programming to quantitative research. My statistical arbitrage engine explores pairs trading, cointegration, and adaptive hedge ratios, alongside hands-on work in robotics and software."
                            : "I'm Kyle Perlman, an electrical and computer engineer interested in quantum engineering and robotics. I build across embedded control, computer vision, and AR, from a physical Connect Four robot to SnapCube on Snapchat Spectacles. I also explore quantitative research through some of my more recent projects."}
                    </motion.p>
                    <p className="hero-interest">{isQuant ? "Interested in quantitative research and quantitative development roles." : "Interested in quantum engineering, robotics, embedded systems, and quantitative research opportunities."}</p>
                    <div className="hero-actions"><a href="#featured">Explore my work</a><Link to={isQuant ? "/" : "/?focus=quant"}>{isQuant ? "View engineering portfolio" : "View quant portfolio"}</Link></div>
                </div>
            </section>

            {/* ABOUT SECTION */}

            {/* FEATURED PROJECT */}
            <FeaturedProject project={featuredProject} focusQuery={focusQuery} />
            {isQuant
                ? <QuantFeature project={quantProject} focusQuery={focusQuery} />
                : <SnapCubeFeature project={snapCube} id="snapcube" />}
            <About themeId={themeId} />

            {/* TIMELINE SECTION */}
            <Timeline projects={projects} focusQuery={focusQuery} />

            {/* SKILLS SECTION */}
            <Skills />

            {/* CONTACT SECTION */}
            <Contact isQuant={isQuant} />

            {/* FOOTER */}
            <footer className="footer" style={{ position: 'relative' }}>
                <a href="/lectures/" className="footer-lectures-link">Lectures</a>
                <ElephantFind id="footer" className="elephant-footer" />
                <p>Copyright {new Date().getFullYear()} {displayName}. All rights reserved.</p>
            </footer>
        </div>
    );
}

export default HomePage;
